import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize first slide state
    this.current = 0;

    this.play();
  }

  // stop next on last slide when infinite=false
  next() {
    if (!this.infinite && this.current >= (this.refs.slides?.length ?? 0) - 1) return;
    this.current += 1;
  }

  // stop previous on first slide when infinite=false
  previous() {
    if (!this.infinite && this.current <= 0) return;
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  // infinite attribute support
  get infinite() {
    const value = this.getAttribute('infinite');
    return value !== 'false';
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const total = (this.refs.slides ?? []).length;
    if (!total) return;

    let relativeIndex;

    if (this.infinite) {
      relativeIndex = current % total;

      if (relativeIndex < 0) {
        relativeIndex += total;
      }
    } else {
      relativeIndex = Math.max(0, Math.min(current, total - 1));
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    // arrow disabled class logic
    if (!this.infinite) {
      const prev = this.refs.previous;
      const next = this.refs.next;

      prev?.classList.remove('announcement-arrow-disabled');
      next?.classList.remove('announcement-arrow-disabled');

      if (relativeIndex === 0) {
        prev?.classList.add('announcement-arrow-disabled');
      }

      if (relativeIndex === total - 1) {
        next?.classList.add('announcement-arrow-disabled');
      }
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}